'use client';

import { Leaf, Scale, Flame } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import type { RenderAmbition } from '@/types/render';

export interface AmbitionContentProps {
  selectedAmbition: RenderAmbition;
  onAmbitionChange: (ambition: RenderAmbition) => void;
  // Layout variant — kept for API parity with the other pickers; both variants
  // share the Apple-grouped row idiom.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
}

export function AmbitionContent({
  selectedAmbition,
  onAmbitionChange,
  closeOnSelect,
}: AmbitionContentProps) {
  const t = useTranslations('render.settings.ambition');

  const ambitions: Array<{
    type: RenderAmbition;
    icon: typeof Scale;
  }> = [
    { type: 'MODEST', icon: Leaf },
    { type: 'BALANCED', icon: Scale },
    { type: 'AMBITIOUS', icon: Flame },
  ];

  const handleSelect = (ambition: RenderAmbition) => {
    onAmbitionChange(ambition);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple parity: grouped card with leading icon + title + description +
          trailing checkmark. Same idiom as the Effort picker. */}
      <SettingsCard>
        {ambitions.map((ambition) => {
          const Icon = ambition.icon;

          return (
            <PickerRow
              key={ambition.type}
              title={t(`types.${ambition.type}.name`)}
              description={t(`types.${ambition.type}.description`)}
              leading={
                <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              }
              isSelected={selectedAmbition === ambition.type}
              onClick={() => handleSelect(ambition.type)}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

/** Row subtitle for the collapsed settings row — the picked level's name. */
export function getAmbitionSubtitle(
  selectedAmbition: RenderAmbition,
  t: (key: string) => string
): string {
  return t(`types.${selectedAmbition}.name`);
}
